import React from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as R from 'ramda';
import { useAsyncFn } from 'react-use';

import { SaveButton } from 'components/UIElements/UIButton';
import CloseButton from 'components/CustomButtons/CloseButton';
import { previousModalPage } from 'actions/modalPage';
import {
  commitTempSettings,
  dropTempSettings,
  saveTempSetting,
} from 'actions/configs';
import {
  getCustomPaymentConfig,
  getCustomPaymentTypes,
} from 'reducers/PaymentTypes';

interface Props {
  paymentType: string;
}

function CustomPaymentConfiguration({ paymentType }: Props) {
  const { t } = useTranslation('settingsCustomPayment');
  const dispatch = useDispatch();

  const paymentTypes = useSelector(getCustomPaymentTypes);
  const customPaymentConfig = useSelector(getCustomPaymentConfig);

  const paymentTypeName = paymentTypes.find(({ type }) => type === paymentType)
    ?.name;

  function onClose() {
    dispatch(dropTempSettings(['custom_payment_configuration']));
    dispatch(previousModalPage());
  }

  function updateCustomPaymentConfiguration(
    key: 'askForReferenceNumber' | 'referenceNumberLabel',
    value: boolean | string,
  ) {
    const updatedCustomPaymentConfig = R.assocPath(
      [paymentType, key],
      value,
      customPaymentConfig,
    );
    dispatch(
      saveTempSetting(
        'custom_payment_configuration',
        JSON.stringify(updatedCustomPaymentConfig),
      ),
    );
  }

  const [{ loading }, onSave] = useAsyncFn(async () => {
    await dispatch(commitTempSettings(['custom_payment_configuration']));
    dispatch(previousModalPage());
  });

  return (
    <div data-testid="custom-payment-setting">
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="1rem"
      >
        <Box fontSize="1.75em" fontWeight={700}>
          {t('configuration.title')}
        </Box>
        <Box flexGrow={1} />
        <SaveButton
          action={onSave}
          disabled={!paymentType || loading}
          variant="POS"
        />
        <CloseButton action={onClose} />
      </Box>
      <Divider />
      <Box padding="1rem">
        {!paymentType ? (
          <Typography variant="h6">{t('configuration.notFound')}</Typography>
        ) : (
          <>
            <Typography variant="h6">{paymentTypeName}</Typography>

            <FormControlLabel
              label={t('configuration.askForReferenceNumber')}
              control={
                <Checkbox
                  checked={
                    customPaymentConfig[paymentType]?.askForReferenceNumber ??
                    false
                  }
                  onChange={e =>
                    updateCustomPaymentConfiguration(
                      'askForReferenceNumber',
                      e.target.checked,
                    )
                  }
                />
              }
            />
            <TextField
              fullWidth
              variant="outlined"
              label={t('configuration.referenceNumberLabel')}
              type="text"
              value={
                customPaymentConfig[paymentType]?.referenceNumberLabel ?? ''
              }
              onChange={e =>
                updateCustomPaymentConfiguration(
                  'referenceNumberLabel',
                  e.target.value,
                )
              }
            />
          </>
        )}
      </Box>
    </div>
  );
}

export default CustomPaymentConfiguration;
